<template>
  <b-container
    class="mb-5"
    fluid
  >
    <b-breadcrumb
      v-if="$route.meta.breadcrumb"
      :items="$route.meta.breadcrumb || []"
    />
    <b-card>
      <b-row class="mb-2">
        <b-col
          cols="12"
        >
          <b-form-group>
            <label>Search String</label>
            <b-input
              v-model="tableSettings.filter"
              type="text"
              placeholder="Enter search string"
              debounce="1000"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-button
            type="button"
            variant="success"
            @click="onShowAddForm"
          >
            Add Duration
          </b-button>
        </b-col>

      </b-row>

      <b-table
        ref="table"
        hover
        responsive
        class="mt-2"
        :per-page="tableSettings.perPage"
        :current-page="tableSettings.currentPage"
        :items="myProvider"
        :fields="tableSettings.fields"
        :sort-by.sync="tableSettings.sortBy"
        :sort-desc.sync="tableSettings.sortDesc"
        :sort-direction="tableSettings.sortDirection"
        :filter="tableSettings.filter"
        :filter-included-fields="tableSettings.filterOn"
        show-empty
      >
        <template #cell(index)="data">
          {{ tableSettings.currentPage * tableSettings.perPage - tableSettings.perPage + (data.index + 1) }}
        </template>

        <template #cell(active)="data">
          {{ data.item.active == 1 ? 'Yes' : 'No' }}
        </template>

        <template #cell()="data">
          <div class="text-nowrap">
            {{ data.value }}
          </div>
        </template>

        <template #cell(actions)="row">
          <div class="text-nowrap">
            <b-button
              size="sm"
              @click="onShowEditForm(row.item)"
            >
              Edit
            </b-button>
          </div>
        </template>

      </b-table>

      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <div class="w-100 w-sm-25 mb-2 sm-mb-2">
            <b-select
              v-model="tableSettings.perPage"
              :options="tableSettings.pageOptions"
              size="sm"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          class="d-flex justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="tableSettings.currentPage"
            :total-rows="tableSettings.totalRows"
            :per-page="tableSettings.perPage"
            first-number
            last-number
            pills
            prev-text="Prev"
            next-text="Next"
            aria-controls="table"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-modal
      id="modal-duration-form"
      scrollable
      no-close-on-backdrop
      :title="modalTitle"
      @ok="onConfirmAction"
    >
      <ValidationObserver
        ref="form"
      >
        <form
          role="form"
          novalidate
        >

          <ValidationProvider
            #default="{ errors }"
            name="duration code"
            vid="duration_code"
            rules="max:30"
          >
            <b-form-group>
              <label for="duration_code">Duration Code</label>
              <b-input
                id="duration_code"
                v-model="duration.duration_code"
                type="text"
                :state="errors.length > 0 ? false : null"
                placeholder="enter duration code"
                autocomplete="off"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
              >
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            #default="{ errors }"
            name="duration name"
            vid="duration_name"
            rules="required|max:100"
          >
            <b-form-group>
              <label for="duration_name">Duration Name</label>
              <b-input
                id="duration_name"
                v-model="duration.duration_name"
                type="text"
                :state="errors.length > 0 ? false : null"
                placeholder="enter duration name"
                autocomplete="off"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
              >
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            #default="{ errors }"
            name="duration minutes"
            vid="duration_minutes"
            rules="required|numeric|min_value:10"
          >
            <b-form-group>
              <label for="duration_minutes">Duration Minutes</label>
              <b-input
                id="duration_minutes"
                v-model="duration.duration_minutes"
                type="number"
                :state="errors.length > 0 ? false : null"
                placeholder="enter duration minutes"
                autocomplete="off"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
              >
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>

          <div
            v-if="editMode"
            class="form-group"
          >
            <b-form-checkbox
              id="is_active"
              v-model="duration.active"
              name="is_active"
              value="1"
              unchecked-value="0"
              :disabled="isBusy"
            >
              Is Active?
            </b-form-checkbox>
          </div>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ok, cancel}">
        <b-button
          variant="success"
          :disabled="isBusy"
          @click="ok()"
        >
          {{ editMode ? 'Update Record' : 'Save Record' }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="isBusy"
          @click="cancel()"
        >
          Cancel
        </b-button>
      </template>

    </b-modal>
  </b-container>
</template>

<script>
import _ from 'lodash'
import { core } from '@/config/pluginInit'
import { AxiosError, AdminDurationService } from '@/services'
import formatter from '@/mixins/formatter'

export default {
  name: 'AdminDurations',

  middleware: ['auth', 'admin'],

  metaInfo () {
    return {
      title: 'Durations'
    }
  },

  mixins: [formatter],

  data () {
    return {
      editMode: false,
      isBusy: false,
      duration: {
        id: '',
        active: 0,
        duration_name: '',
        duration_code: '',
        duration_minutes: 0
      },
      tableSettings: {
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
        totalRows: 0,
        currentPage: 1,
        sortBy: '',
        sortDesc: false,
        sortDirection: 'asc',
        filter: '',
        filterOn: [],
        fields: [
          'index',
          'actions',
          { key: 'created_at', formatter: this.dateTimeFormatter, sortable: true },
          { key: 'duration_code', sortable: true },
          { key: 'duration_name', sortable: true },
          { key: 'duration_minutes', sortable: true },
          { key: 'active', label: 'IS ACTIVE?' },
          { key: 'updated_at', formatter: this.dateTimeFormatter, sortable: true }
        ]
      }
    }
  },

  computed: {
    modalTitle () {
      return this.editMode ? 'Edit Duration' : 'Add Duration'
    }
  },

  mounted () {
    core.index()
  },

  methods: {
    async myProvider (ctx) {
      try {
        const { data } = await AdminDurationService.get(`page=${ctx.currentPage}&per_page=${ctx.perPage}&sort=${ctx.sortBy}&sort_desc=${ctx.sortDesc}&filter_text=${ctx.filter}`)
        this.tableSettings.totalRows = data.total_rows
        return data.items
      } catch {
        return []
      }
    },

    onShowAddForm () {
      this.editMode = false
      this.duration.id = 0
      this.duration.active = 1
      this.duration.duration_code = ''
      this.duration.duration_name = ''
      this.duration.duration_minutes = 0
      this.$bvModal.show('modal-duration-form')
    },

    onShowEditForm (item) {
      this.editMode = true
      this.duration.id = item.id
      this.duration.active = item.active
      this.duration.duration_code = item.duration_code
      this.duration.duration_name = item.duration_name
      this.duration.duration_minutes = item.duration_minutes
      this.$bvModal.show('modal-duration-form')
    },

    async onConfirmAction (bvModalEvt) {
      bvModalEvt.preventDefault()
      await this.$refs.form.validate().then(async success => {
        if (success) {
          let title = 'Do you really want to create duration?'
          if (this.editMode) {
            title = 'Do you really want to save changes?'
          }
          this.$swal.fire({
            icon: 'question',
            title: title,
            confirmButtonColor: '#06C270',
            confirmButtonText: 'Yes',
            cancelButtonColor: '#FF2929',
            showCancelButton: true,
            showLoaderOnConfirm: true,
            preConfirm: () => {
              if (!this.editMode) {
                return this.onFormPostSubmit()
              } else {
                return this.onFormPutSubmit()
              }
            },
            allowOutsideClick: () => !this.$swal.isLoading()
          })
        } else {
          this.$bvModal.msgBoxOk('Oppss! There were problem with your inputs.', {
            title: 'Validation Error',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger'
          })
        }
      })
    },

    async onFormPostSubmit () {
      return new Promise((resolve, reject) => {
        this.isBusy = true
        AdminDurationService.post(this.duration).then(({ data }) => {
          this.$bvModal.hide('modal-duration-form')
          this.isBusy = false
          this.$swal.fire({
            icon: 'success',
            title: 'Successful',
            text: data.message,
            confirmButtonColor: '#06C270',
            confirmButtonText: 'Dismiss'
          }).then(() => {
            this.$refs.table.refresh()
          })
        }).catch(error => {
          this.isBusy = false
          if (error instanceof AxiosError) {
            if (error.code === 422) {
              this.$refs.form.setErrors(error.message)
              this.$bvModal.msgBoxOk('Oppss! There were problem with your inputs.', {
                title: 'Validation Error',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger'
              })
              resolve(error)
            }
          }
        })
      })
    },

    async onFormPutSubmit () {
      return new Promise((resolve, reject) => {
        this.isBusy = true
        AdminDurationService.put(this.duration).then(({ data }) => {
          this.$bvModal.hide('modal-duration-form')
          this.isBusy = false
          this.$swal.fire({
            icon: 'success',
            title: 'Successful',
            text: data.message,
            confirmButtonColor: '#06C270',
            confirmButtonText: 'Dismiss'
          }).then(() => {
            const row = _.find(this.$refs.table.localItems, { id: this.duration.id })
            row.active = data.duration.active
            row.duration_code = data.duration.duration_code
            row.duration_name = data.duration.duration_name
            row.created_at = data.duration.created_at
            row.updated_at = data.duration.updated_at
          })
        }).catch(error => {
          this.isBusy = false
          if (error instanceof AxiosError) {
            if (error.code === 422) {
              this.$refs.form.setErrors(error.message)
              this.$bvModal.msgBoxOk('Oppss! There were problem with your inputs.', {
                title: 'Validation Error',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger'
              })
              resolve(error)
            }
          }
        })
      })
    }
  }
}
</script>
